/**
 * Utility untuk mapping template_key ke component name
 * Format: {component_type}-{number} -> {Prefix}Variant{Number}
 *
 * Mapping rules:
 * - header-4 -> HVariantFour
 * - banner-2 -> BVariantTwo
 * - category-1 -> CVariantOne
 * - product-category-1 -> PCVariantOne
 * - product-showcase-2 -> ColVariantTwo (product-showcase adalah collection)
 * - product-list-1 -> PLVariantOne
 * - testimonial-1 -> TVariantOne
 * - feature-1 -> FsVariantOne
 * - brand-2 -> BrVariantTwo
 * - cta-1 -> CTAOne
 * - footer-1 -> FVariantOne
 */

// Mapping prefix untuk setiap component type
const COMPONENT_PREFIXES = {
  header: "H",
  banner: "B",
  category: "C",
  "product-category": "PC",
  "product-showcase": "Col", // product-showcase adalah collection, langsung pakai Col
  "product-list": "PL",
  testimonial: "T",
  feature: "Fs",
  brand: "Br",
  blog: "Bl",
  cta: "CTA",
  footer: "F",
  collection: "Col",
  "landing-content": "LC",
};

// Mapping untuk CTA yang tidak menggunakan "Variant"
const CTA_COMPONENT_NAMES = {
  "cta-1": "CTAOne",
  "cta-2": "CTATwo",
  "cta-3": "CTAThree",
  "cta-4": "CTAFour",
};

/**
 * Convert number to word (1 -> One, 2 -> Two, etc.)
 */
const numberToWord = (num) => {
  const words = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
  ];
  return words[num] || `Variant${num}`;
};

/**
 * Convert template_key ke component name
 * @param {string} templateKey - Template key dari API (e.g., "header-4", "banner-2")
 * @returns {string} Component name (e.g., "HVariantFour", "BVariantTwo")
 */
export const getComponentName = (templateKey) => {
  if (!templateKey || typeof templateKey !== "string") {
    return null;
  }

  // Handle CTA khusus (tidak pakai Variant)
  if (CTA_COMPONENT_NAMES[templateKey]) {
    return CTA_COMPONENT_NAMES[templateKey];
  }

  // Parse template_key: "header-4" -> ["header", "4"]
  const parts = templateKey.split("-");
  if (parts.length < 2) {
    return null;
  }

  const componentType = parts.slice(0, -1).join("-"); // "product-category" atau "header"
  const variantNumber = parseInt(parts[parts.length - 1], 10);

  if (isNaN(variantNumber)) {
    return null;
  }

  const prefix = COMPONENT_PREFIXES[componentType];
  if (!prefix) {
    return null;
  }

  const variantWord = numberToWord(variantNumber);
  return `${prefix}Variant${variantWord}`;
};

/**
 * Get component import path berdasarkan component name
 * @param {string} componentName - Component name (e.g., "HVariantFour")
 * @param {string} componentType - Component type (e.g., "header")
 * @returns {string} Import path
 */
export const getComponentPath = (componentName, componentType) => {
  if (!componentName || !componentType) {
    return null;
  }

  // Mapping folder names
  const folderMap = {
    header: "header",
    banner: "banner",
    category: "category",
    "product-category": "product-category",
    "product-showcase": "collection", // product-showcase adalah collection
    "product-list": "product-list",
    testimonial: "testimonial",
    feature: "features",
    brand: "brand",
    blog: "blog",
    cta: "cta",
    footer: "footer",
    collection: "collection",
    "landing-content": "landing-content",
  };

  const folder = folderMap[componentType] || componentType;
  return `@/components/${folder}/${componentName}`;
};

/**
 * Get all components dari ui_components config, sorted by sort_order
 * @param {Object} uiComponents - ui_components object dari config
 * @returns {Array} Array of component configs sorted by sort_order
 */
export const getSortedComponents = (uiComponents) => {
  if (!uiComponents || typeof uiComponents !== "object") {
    return [];
  }

  const allComponents = [];

  // Iterate semua component types
  Object.keys(uiComponents).forEach((componentType) => {
    const components = uiComponents[componentType];
    if (Array.isArray(components)) {
      components.forEach((component) => {
        if (component && component.template_key) {
          allComponents.push({
            ...component,
            componentType,
          });
        }
      });
    }
  });

  // Sort by sort_order (default 0 jika tidak ada)
  return allComponents.sort((a, b) => {
    const orderA = a.sort_order ?? 0;
    const orderB = b.sort_order ?? 0;
    return orderA - orderB;
  });
};
